/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  BarChart3,
  Calendar,
  CalendarClock,
  ClipboardList,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  FolderOpen,
  BellRing,
  Home,
  Inbox,
  LayoutDashboard,
  type LucideIcon,
  MessageSquare,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Stethoscope,
  Target,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';

export interface NavItem {
  /** i18n key under `nav.` */
  readonly labelKey: string;
  readonly path: string;
  readonly icon: LucideIcon;
  /** RBAC module key (for custom-role nav-gating); omit = always visible. */
  readonly module?: string;
}

/** Navigation mirrors the Figma sidebars exactly — order matters. */
export const ADMIN_NAV: readonly NavItem[] = [
  { labelKey: 'dashboard', path: '/admin', icon: LayoutDashboard, module: 'dashboard' },
  { labelKey: 'inbox', path: '/admin/inbox', icon: Inbox, module: 'inbox' },
  { labelKey: 'clients', path: '/admin/clients', icon: Users, module: 'patients' },
  { labelKey: 'therapists', path: '/admin/therapists', icon: Stethoscope, module: 'therapists' },
  { labelKey: 'waitlist', path: '/admin/waitlist', icon: ClipboardList, module: 'waitlist' },
  { labelKey: 'users', path: '/admin/users', icon: UserCog, module: 'users' },
  { labelKey: 'schedule', path: '/admin/sessions', icon: Calendar, module: 'schedule' },
  {
    labelKey: 'sessionRequests',
    path: '/admin/session-requests',
    icon: CalendarClock,
    module: 'schedule',
  },
  { labelKey: 'followUps', path: '/admin/follow-ups', icon: BellRing, module: 'followUps' },
  { labelKey: 'reports', path: '/admin/reports', icon: FileText, module: 'reports' },
  { labelKey: 'finance', path: '/admin/finance', icon: DollarSign, module: 'invoices' },
  { labelKey: 'proformas', path: '/admin/proformas', icon: ScrollText, module: 'proformas' },
  {
    labelKey: 'consultantInvoices',
    path: '/admin/consultant-invoices',
    icon: Wallet,
    module: 'invoices',
  },
  { labelKey: 'analytics', path: '/admin/analytics', icon: TrendingUp, module: 'analytics' },
  { labelKey: 'settings', path: '/admin/settings', icon: Settings, module: 'settings' },
];

export const THERAPIST_NAV: readonly NavItem[] = [
  { labelKey: 'dashboard', path: '/therapist', icon: LayoutDashboard, module: 'dashboard' },
  { labelKey: 'myClients', path: '/therapist/clients', icon: Users, module: 'patients' },
  { labelKey: 'schedule', path: '/therapist/schedule', icon: Calendar, module: 'schedule' },
  {
    labelKey: 'availability',
    path: '/therapist/availability',
    icon: CalendarClock,
    module: 'schedule',
  },
  { labelKey: 'sessions', path: '/therapist/sessions', icon: ClipboardList, module: 'sessions' },
  { labelKey: 'sessionNotes', path: '/therapist/notes', icon: FileText, module: 'soapNotes' },
  { labelKey: 'therapyPlans', path: '/therapist/plans', icon: Target, module: 'sessions' },
  { labelKey: 'timesheet', path: '/therapist/timesheet', icon: Clock, module: 'timesheet' },
  {
    labelKey: 'consultantInvoices',
    path: '/therapist/invoices',
    icon: Receipt,
    module: 'invoices',
  },
  { labelKey: 'documents', path: '/therapist/documents', icon: FolderOpen, module: 'documents' },
  { labelKey: 'messages', path: '/therapist/messages', icon: MessageSquare, module: 'inbox' },
];

export const PARENT_NAV: readonly NavItem[] = [
  // Mirrors the Figma Parent Portal sidebar (6 items, this order), plus the
  // intake + reports surfaces (the intake→admin-PDF flow is a TherapyOS
  // extension the Figma doesn't cover — the patient must reach both).
  { labelKey: 'dashboard', path: '/parent', icon: LayoutDashboard },
  { labelKey: 'therapyPlan', path: '/parent/progress', icon: ClipboardList },
  { labelKey: 'sessions', path: '/parent/sessions', icon: Calendar },
  { labelKey: 'billing', path: '/parent/billing', icon: CreditCard },
  { labelKey: 'appointments', path: '/parent/book', icon: CalendarClock },
  { labelKey: 'homeProgramme', path: '/parent/home-tasks', icon: Home },
  { labelKey: 'patientDetails', path: '/parent/patient-details', icon: ScrollText },
  { labelKey: 'reports', path: '/parent/reports', icon: FileText },
];

export const FINANCE_NAV: readonly NavItem[] = [
  // Mirrors the Figma Finance sidebar (Dashboard · Inbox · Proformas · Invoices ·
  // Payroll). Catalogue/Reports/Analytics remain as routes but are off the nav.
  { labelKey: 'dashboard', path: '/finance', icon: LayoutDashboard },
  { labelKey: 'inbox', path: '/finance/inbox', icon: Inbox },
  { labelKey: 'proformas', path: '/finance/proformas', icon: ScrollText },
  { labelKey: 'invoices', path: '/finance/invoices', icon: Receipt },
  { labelKey: 'payroll', path: '/finance/payroll', icon: Wallet },
];

/** Staff console — designed by us (no Figma source), neutral-dark accent. */
export const SA_NAV: readonly NavItem[] = [
  { labelKey: 'dashboard', path: '/sa', icon: LayoutDashboard },
  { labelKey: 'clinics', path: '/sa/clinics', icon: Users },
  { labelKey: 'billing', path: '/sa/subscriptions', icon: CreditCard },
  { labelKey: 'analytics', path: '/sa/usage', icon: BarChart3 },
  { labelKey: 'staffUsers', path: '/sa/staff-users', icon: ShieldCheck },
  { labelKey: 'reports', path: '/sa/audit', icon: FileText },
  { labelKey: 'settings', path: '/sa/settings', icon: Settings },
];
